// src/kernel/messages.ts
import { z } from "zod";
var MAX_MESSAGE_KEY_LENGTH = 200;
var messageRefSchema = z.object({
  key: z.templateLiteral([z.string(), ".", z.string()]),
  params: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).readonly().optional()
}).readonly();
function msg(key, params) {
  return params ? { key, params } : { key };
}
var declared = /* @__PURE__ */ new Set();
function defineMessages(namespace, keys) {
  const table = {};
  for (const name of keys) {
    table[name] = `${namespace}.${name}`;
    declared.add(`${namespace}.${name}`);
  }
  return Object.freeze(table);
}
function declaredMessageKeys() {
  return declared;
}

// src/domains/core-messages.ts
var CORE_MESSAGES = defineMessages("core", [
  // Transport / auth
  "internalError",
  "serviceUnavailable",
  "notAuthenticated",
  "forbidden",
  "notFound",
  "alreadyExists",
  "validationFailed",
  "payloadTooLarge",
  "tooManyRequests",
  "invalidCredentials",
  // Generic UI verbs, reused by every module
  "cancel",
  "noMatches",
  "create",
  "enable",
  "disable",
  "delete",
  "save",
  "saving",
  "saved",
  "edit",
  "view",
  "confirm",
  "connect",
  "connected",
  "retry",
  "copy",
  "copied",
  "loading",
  "signOut",
  "language",
  "toggleSidebar",
  "columnFile",
  "columnStatus",
  "columnRows",
  "columnRow",
  "columnUpdated",
  "rowsLabel",
  "rowsPerPage",
  "pageOf",
  "rangeOf",
  "firstPage",
  "previousPage",
  "nextPage",
  "lastPage",
  "name",
  "email",
  "password",
  "confirmPassword"
]);
var VALIDATION_MESSAGES = defineMessages("validation", [
  "required",
  "invalid",
  "tooShort",
  "tooLong",
  "invalidEmail",
  "maxLength",
  "notANumber",
  "maxDecimals",
  "wholeNumber",
  "positive",
  "notNegative",
  "notAnOption",
  "invalidDate",
  "passwordMismatch"
]);

// src/domains/identity/messages.ts
var IDENTITY_MESSAGES = defineMessages("identity", [
  "signInTitle",
  "signInSubtitle",
  "signInSubmit",
  "signInSso",
  "ssoNotConfigured",
  "roleOwner",
  "roleAdmin",
  "roleMember",
  "rolePlatformAdmin",
  "platformAdminRequired",
  "orgRequired",
  "moduleRequired",
  "navDashboard",
  "navOrganizations",
  "navUsers",
  "navSso",
  "navPlatformSso",
  "navAdministration",
  "dashboardTitle",
  "dashboardWelcome",
  "organizationsTitle",
  "organizationsEmpty",
  "createOrganization",
  "roleLabel",
  "modulesLabel",
  "organizationSlug",
  "adminEmail",
  "adminName",
  "disableOrganization",
  "disableOrganizationConfirm",
  "enableOrganization",
  "organizationDisabled",
  "usersTitle",
  "usersEmpty",
  "memberDisabled",
  "disableMemberConfirm",
  "accountDisabled",
  "createRole",
  "ssoTitle",
  "ssoConfigure",
  "ssoEmpty",
  "ssoPending",
  "ssoVerify",
  "ssoDisconnect",
  "ssoDisconnectConfirm",
  "ssoProviderId",
  "ssoProviderEntra",
  "ssoTenantId",
  "ssoIssuer",
  "ssoDomain",
  "ssoClientId",
  "ssoClientSecret",
  "ssoClientSecretHint",
  "ssoRedirectUri",
  "ssoRedirectUriHint",
  "ssoDnsHint",
  "ssoTxtName",
  "ssoTxtNameHint",
  "ssoTxtValue",
  "ssoDomainVerifyFailed",
  "ssoDomainVerifyNotFound",
  "ssoDomainVerifyMismatch",
  "ssoConnectFailed",
  "signInFailed",
  "tooManySignInAttempts",
  "resetEmailSubject",
  "resetEmailBody",
  "emailSecretRequired",
  "emailNotConfigured",
  "emailTestSubject",
  "emailTestBody",
  "navEmail",
  "navPlatformEmail",
  "emailTitle",
  "emailConfigure",
  "emailProvider",
  "emailProviderGraph",
  "emailProviderSmtp",
  "emailTenantId",
  "emailClientId",
  "emailClientSecret",
  "emailClientSecretHint",
  "emailHost",
  "emailSmtpMode",
  "emailSmtpStarttls",
  "emailSmtpTls",
  "emailUsername",
  "emailPassword",
  "emailPasswordHint",
  "emailFromAddress",
  "emailEnabled",
  "emailEnabledYes",
  "emailEnabledNo",
  "emailTest",
  "emailTestTo",
  "emailTestOk",
  "navProfile",
  "profileTitle",
  "profileAccount",
  "profileName",
  "profileAvatar",
  "profileChangeAvatar",
  "profilePassword",
  "profilePasswordHint",
  "profileSendReset",
  "profileResetSent",
  "setPasswordTitle",
  "setPasswordSubmit",
  "setPasswordInvalidToken",
  "setPasswordSuccess",
  "mfaTitle",
  "mfaHint",
  "mfaEnabled",
  "mfaEnable",
  "mfaDisable",
  "mfaDisableConfirm",
  "mfaPasswordConfirm",
  "mfaTotpSecret",
  "mfaTotpSecretHint",
  "mfaTotpQr",
  "mfaTotpCode",
  "mfaVerify",
  "mfaBackupCodes",
  "mfaBackupCodesHint",
  "mfaRegenerateBackup",
  "mfaInvalidCode",
  "mfaInvalidPassword",
  "mfaEnrolTitle",
  "mfaEnrolHint",
  "twoFactorTitle",
  "twoFactorSubtitle",
  "twoFactorSubmit",
  "twoFactorBackup",
  "twoFactorUseTotp",
  "passkeyTitle",
  "passkeyHint",
  "passkeyEmpty",
  "passkeyAdd",
  "passkeyName",
  "passkeyRename",
  "passkeyDelete",
  "passkeyDeleteConfirm",
  "passkeyFailed",
  "signInPasskey",
  "navAuthPolicy",
  "authPolicyTitle",
  "authPolicyHint",
  "authPolicyMethods",
  "authPolicyMethodPassword",
  "authPolicyMethodSso",
  "authPolicyMethodPasskey",
  "authPolicyPasswordMfa",
  "authPolicyMfaOff",
  "authPolicyMfaOptional",
  "authPolicyMfaRequired",
  "resetMfa",
  "resetMfaConfirm",
  "inviteHint",
  "inviteSent",
  "sendInvite",
  "inviteUser",
  "createPlatformAdmin",
  "sendOwnerReset",
  "sendOwnerResetConfirm"
]);

// src/domains/identity/sso-providers.ts
var SSO_PROVIDERS = {
  entra: {
    id: "entra",
    titleKey: IDENTITY_MESSAGES.ssoProviderEntra,
    issuer: (tenantId) => `https://login.microsoftonline.com/${tenantId}/v2.0`,
    discoveryOrigins: ["https://login.microsoftonline.com", "https://graph.microsoft.com"]
  }
};
var SSO_PROVIDER_IDS = Object.keys(SSO_PROVIDERS);
var PLATFORM_SSO_PROVIDER_PREFIX = "platform-";
function baSsoProviderId(scope, catalogId) {
  return scope === "platform" ? `${PLATFORM_SSO_PROVIDER_PREFIX}${catalogId}` : catalogId;
}
function ssoOidcRedirectUri(apiOrigin, scope, catalogId) {
  return `${apiOrigin.replace(/\/$/, "")}/api/auth/sso/callback/${baSsoProviderId(scope, catalogId)}`;
}
function catalogSsoProviderId(baProviderId) {
  const id = baProviderId.startsWith(PLATFORM_SSO_PROVIDER_PREFIX) ? baProviderId.slice(PLATFORM_SSO_PROVIDER_PREFIX.length) : baProviderId;
  return id in SSO_PROVIDERS ? id : null;
}
function ssoIssuerFor(providerId, tenantId) {
  return SSO_PROVIDERS[providerId].issuer(tenantId);
}
function tenantIdFromIssuer(providerId, issuer) {
  if (providerId === "entra") {
    const match = /^https:\/\/login\.microsoftonline\.com\/([^/]+)\/v2\.0\/?$/i.exec(issuer.trim());
    return match?.[1] ?? "";
  }
  return "";
}
function ssoDiscoveryOrigins() {
  return [...new Set(Object.values(SSO_PROVIDERS).flatMap((provider) => [...provider.discoveryOrigins]))];
}

// src/kernel/locale.ts
import { z as z2 } from "zod";
var LOCALES = ["en-GB", "nl-NL"];
var MAX_LOCALE_LENGTH = 10;
var DEFAULT_LOCALE = "en-GB";
var localeSchema = z2.enum(LOCALES);
function languageOf(tag) {
  try {
    return new Intl.Locale(tag).language;
  } catch (error) {
    console.warn(`[i18n] Ignoring invalid locale tag "${tag}"`, error);
    return void 0;
  }
}
function matchLocale(tag) {
  if (!tag) return DEFAULT_LOCALE;
  const exact = LOCALES.find((locale) => locale === tag);
  if (exact) return exact;
  const language = languageOf(tag);
  return LOCALES.find((locale) => new Intl.Locale(locale).language === language) ?? DEFAULT_LOCALE;
}

// src/kernel/errors.ts
import { z as z3 } from "zod";
var ERROR_CODES = {
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  NOT_FOUND: "NOT_FOUND",
  ALREADY_EXISTS: "ALREADY_EXISTS",
  INVALID_INPUT: "INVALID_INPUT",
  INVALID_STATE: "INVALID_STATE",
  PAYLOAD_TOO_LARGE: "PAYLOAD_TOO_LARGE",
  TOO_MANY_REQUESTS: "TOO_MANY_REQUESTS",
  INTERNAL_ERROR: "INTERNAL_ERROR",
  SERVICE_UNAVAILABLE: "SERVICE_UNAVAILABLE"
};
var ERROR_STATUS = {
  [ERROR_CODES.UNAUTHORIZED]: 401,
  [ERROR_CODES.FORBIDDEN]: 403,
  [ERROR_CODES.NOT_FOUND]: 404,
  [ERROR_CODES.ALREADY_EXISTS]: 409,
  [ERROR_CODES.INVALID_INPUT]: 400,
  [ERROR_CODES.INVALID_STATE]: 409,
  [ERROR_CODES.PAYLOAD_TOO_LARGE]: 413,
  [ERROR_CODES.TOO_MANY_REQUESTS]: 429,
  [ERROR_CODES.INTERNAL_ERROR]: 500,
  [ERROR_CODES.SERVICE_UNAVAILABLE]: 503
};
var fieldIssueSchema = z3.object({ field: z3.string(), message: messageRefSchema }).readonly();
var AppError = class _AppError extends Error {
  code;
  status;
  messageRef;
  issues;
  constructor(code, messageRef, issues) {
    super(`${code}: ${messageRef.key}`);
    this.name = "AppError";
    this.code = code;
    this.status = ERROR_STATUS[code];
    this.messageRef = messageRef;
    this.issues = issues;
    Error.captureStackTrace?.(this, _AppError);
  }
};
function isAppError(error) {
  return error instanceof AppError;
}
var ErrorFactories = {
  unauthorized: (ref = msg(CORE_MESSAGES.notAuthenticated)) => new AppError(ERROR_CODES.UNAUTHORIZED, ref),
  forbidden: (ref = msg(CORE_MESSAGES.forbidden)) => new AppError(ERROR_CODES.FORBIDDEN, ref),
  notFound: (ref = msg(CORE_MESSAGES.notFound)) => new AppError(ERROR_CODES.NOT_FOUND, ref),
  alreadyExists: (ref = msg(CORE_MESSAGES.alreadyExists)) => new AppError(ERROR_CODES.ALREADY_EXISTS, ref),
  invalidInput: (ref = msg(CORE_MESSAGES.validationFailed), issues) => new AppError(ERROR_CODES.INVALID_INPUT, ref, issues),
  invalidState: (ref) => new AppError(ERROR_CODES.INVALID_STATE, ref),
  payloadTooLarge: (ref = msg(CORE_MESSAGES.payloadTooLarge)) => new AppError(ERROR_CODES.PAYLOAD_TOO_LARGE, ref),
  tooManyRequests: (ref = msg(CORE_MESSAGES.tooManyRequests)) => new AppError(ERROR_CODES.TOO_MANY_REQUESTS, ref),
  internal: (ref = msg(CORE_MESSAGES.internalError)) => new AppError(ERROR_CODES.INTERNAL_ERROR, ref),
  serviceUnavailable: (ref = msg(CORE_MESSAGES.serviceUnavailable)) => new AppError(ERROR_CODES.SERVICE_UNAVAILABLE, ref)
};
function toMessageRef(error, fallback = msg(CORE_MESSAGES.internalError)) {
  return isAppError(error) ? error.messageRef : fallback;
}

// src/kernel/module.ts
var MAX_ID_LENGTH = 64;
function createRegistry(kind) {
  const entries = /* @__PURE__ */ new Map();
  return {
    register(entry) {
      if (entries.has(entry.manifest.id)) {
        console.error(`[modules] ${kind} module "${entry.manifest.id}" registered twice`);
        throw ErrorFactories.internal();
      }
      entries.set(entry.manifest.id, entry);
    },
    all: () => [...entries.values()],
    get: (id) => entries.get(id)
  };
}

// src/domains/identity/require-organization.ts
function requireOrganizationId(user) {
  if (!user.organizationId) throw ErrorFactories.forbidden(msg(IDENTITY_MESSAGES.orgRequired));
  return user.organizationId;
}

// src/domains/identity/user.ts
import { z as z4 } from "zod";
var PLATFORM_ROLES = ["platform_admin", "user"];
var PLATFORM_ROLE = {
  PLATFORM_ADMIN: "platform_admin",
  USER: "user"
};
var ORG_ROLE = {
  OWNER: "owner",
  ADMIN: "admin",
  MEMBER: "member"
};
var ORG_ROLE_LABEL = {
  [ORG_ROLE.OWNER]: IDENTITY_MESSAGES.roleOwner,
  [ORG_ROLE.ADMIN]: IDENTITY_MESSAGES.roleAdmin,
  [ORG_ROLE.MEMBER]: IDENTITY_MESSAGES.roleMember
};
var ORG_RESOURCE = {
  MEMBER: "member",
  ROLES: "ac",
  ORGANIZATION: "organization"
};
var ACTION = {
  READ: "read",
  CREATE: "create",
  UPDATE: "update",
  DELETE: "delete"
};
var idsSchema = z4.array(z4.string()).readonly();
var permissionsSchema = z4.record(z4.string(), idsSchema).readonly();
function can(permissions, resource, action) {
  return permissions[resource]?.includes(action) ?? false;
}
var AUTH_METHODS = ["sso", "password", "passkey"];
var PASSWORD_MFA = ["off", "optional", "required"];
var organizationAuthPolicySchema = z4.object({
  allowedMethods: z4.array(z4.enum(AUTH_METHODS)).min(1),
  /** Authenticator (TOTP) requirement for password sign-in only. */
  passwordMfa: z4.enum(PASSWORD_MFA)
}).readonly();
var DEFAULT_ORG_AUTH_POLICY = {
  allowedMethods: ["password", "sso", "passkey"],
  passwordMfa: "off"
};
var PLATFORM_AUTH_POLICY = {
  allowedMethods: ["password", "sso", "passkey"],
  passwordMfa: "required"
};
function resolveCompliant(status, policy) {
  if (status.isSso) return true;
  if (policy.passwordMfa !== "required") return true;
  return status.hasTotp;
}
var authUserSchema = z4.object({
  id: z4.uuid(),
  email: z4.string(),
  name: z4.string(),
  /** Avatar URL, or null. */
  image: z4.string().nullable(),
  role: z4.enum(PLATFORM_ROLES),
  organizationId: z4.uuid().nullable(),
  organizationName: z4.string().nullable(),
  /** Roles held in the active organisation (empty without one). */
  roles: idsSchema,
  /** Union of those roles' grants, resolved once per request. */
  permissions: permissionsSchema,
  /** The user's own choice, else the default — never the organisation's. */
  locale: localeSchema,
  modules: idsSchema,
  /** Credential / MFA enrolment status (drives compliance). */
  authStatus: z4.object({
    hasPassword: z4.boolean(),
    hasTotp: z4.boolean(),
    /** Verified SSO for this caller's scope + they belong to it — MFA/password are the IdP's. */
    isSso: z4.boolean()
  }).readonly(),
  /** Resolved auth policy (org row or platform fixed / org default). */
  authPolicy: organizationAuthPolicySchema,
  /** Server-derived: meets `authPolicy` (SSO always true). */
  compliant: z4.boolean()
}).readonly();
var callerSessionSchema = z4.object({
  user: authUserSchema.nullable(),
  denied: z4.literal(IDENTITY_MESSAGES.accountDisabled).optional()
}).readonly();
var organizationSummarySchema = z4.object({ id: z4.uuid(), name: z4.string(), slug: z4.string(), isActive: z4.boolean(), modules: idsSchema }).readonly();
var orgMemberSummarySchema = z4.object({
  /** Member id (not the user id). */
  id: z4.uuid(),
  email: z4.string(),
  name: z4.string(),
  roles: idsSchema,
  /** False when the org admin deactivated this membership. */
  isActive: z4.boolean()
}).readonly();
var orgRoleSummarySchema = z4.object({
  /** Present for organisation-defined roles only. */
  id: z4.uuid().nullable(),
  role: z4.string(),
  permissions: permissionsSchema
}).readonly();
var dnsTxtRecordSchema = z4.object({
  /** The name within the domain (`_better-auth-token-<provider>`), as DNS panels ask for it; the full name adds `.<domain>`. */
  name: z4.string(),
  value: z4.string()
}).readonly();
var ssoProviderSummarySchema = z4.object({
  providerId: z4.enum(SSO_PROVIDER_IDS),
  issuer: z4.string(),
  domain: z4.string(),
  domainVerified: z4.boolean(),
  /** OIDC client id when stored; never the secret. */
  clientId: z4.string().nullable(),
  /** The TXT record to publish while domain verification is pending; null once verified. */
  domainVerification: dnsTxtRecordSchema.nullable()
}).readonly();

// src/domains/identity/index.ts
import { z as z5 } from "zod";
var IDENTITY_LIMITS = {
  MAX_NAME_LENGTH: 200,
  MAX_EMAIL_LENGTH: 255,
  MAX_SLUG_LENGTH: 100,
  MAX_PASSWORD_LENGTH: 200,
  MAX_DOMAIN_LENGTH: 255,
  MAX_TENANT_ID_LENGTH: 100,
  /** An IdP client id or secret. */
  MAX_CLIENT_CREDENTIAL_LENGTH: 500
};
var passwordSchema = z5.string().min(8).max(IDENTITY_LIMITS.MAX_PASSWORD_LENGTH);
var emailSchema = z5.email().max(IDENTITY_LIMITS.MAX_EMAIL_LENGTH);
var nameSchema = z5.string().trim().min(1).max(IDENTITY_LIMITS.MAX_NAME_LENGTH);
var moduleIdsSchema = z5.array(z5.string().min(1).max(MAX_ID_LENGTH));
var identifierSchema = z5.string().trim().min(1).max(MAX_ID_LENGTH).regex(/^[a-z][a-z0-9-]*$/);
var rolesSchema = z5.array(identifierSchema).min(1);
var organizationSlugSchema = z5.string().trim().min(1).max(IDENTITY_LIMITS.MAX_SLUG_LENGTH).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
var createOrganizationSchema = z5.object({
  name: nameSchema,
  slug: organizationSlugSchema,
  adminEmail: emailSchema,
  adminName: nameSchema,
  modules: moduleIdsSchema.default([])
});
var editOrganizationSchema = z5.object({
  name: nameSchema,
  slug: organizationSlugSchema
});
var updateOrgModulesSchema = z5.object({
  modules: moduleIdsSchema
});
var createOrgUserSchema = z5.object({
  email: emailSchema,
  name: nameSchema,
  roles: rolesSchema
});
var createPlatformAdminSchema = z5.object({
  email: emailSchema,
  name: nameSchema
});
var createOrganizationFormSchema = createOrganizationSchema.extend({ modules: moduleIdsSchema });
var updateMemberSchema = z5.object({
  roles: rolesSchema.optional(),
  isActive: z5.boolean().optional()
}).refine((input) => input.roles !== void 0 || input.isActive !== void 0, {
  message: VALIDATION_MESSAGES.required
});
var memberRolesFormSchema = z5.object({ roles: rolesSchema });
var orgRoleFormSchema = z5.object({ role: identifierSchema, modules: moduleIdsSchema.min(1) });
var orgRolePermissionsSchema = z5.object({
  permissions: z5.record(z5.string().min(1).max(MAX_ID_LENGTH), z5.array(z5.string().min(1).max(MAX_ID_LENGTH)))
});
var orgRoleSchema = orgRolePermissionsSchema.extend({ role: identifierSchema });
var ssoProviderSchema = z5.object({
  providerId: z5.enum(SSO_PROVIDER_IDS),
  tenantId: z5.string().trim().min(1).max(IDENTITY_LIMITS.MAX_TENANT_ID_LENGTH),
  /** An email domain: zod's hostname rules, and at least one dot (no bare `localhost`, no trailing dot). */
  domain: z5.string().trim().toLowerCase().max(IDENTITY_LIMITS.MAX_DOMAIN_LENGTH).pipe(z5.hostname().refine((host) => host.includes(".") && !host.endsWith("."))),
  clientId: z5.string().trim().min(1).max(IDENTITY_LIMITS.MAX_CLIENT_CREDENTIAL_LENGTH),
  /** Required when creating; omit or leave empty on update to keep the stored secret. */
  clientSecret: z5.string().max(IDENTITY_LIMITS.MAX_CLIENT_CREDENTIAL_LENGTH).optional().default("")
});
var ssoProviderFormSchema = (isExisting) => ssoProviderSchema.superRefine((input, ctx) => {
  if (!isExisting && !input.clientSecret.trim()) {
    ctx.addIssue({ code: "custom", message: VALIDATION_MESSAGES.required, path: ["clientSecret"] });
  }
});
var signInFieldsSchema = z5.object({
  email: emailSchema,
  password: z5.string()
});
var signInFormSchema = z5.object({
  email: emailSchema,
  password: z5.string().min(1)
});
var ssoAvailableQuerySchema = z5.object({ email: emailSchema });
var ssoAvailableSchema = z5.object({ available: z5.boolean() }).readonly();
function emailDomain(email) {
  const at = email.lastIndexOf("@");
  if (at < 0 || at === email.length - 1) return null;
  return email.slice(at + 1).trim().toLowerCase() || null;
}
var updateOrganizationSchema = z5.object({
  name: nameSchema.optional(),
  slug: organizationSlugSchema.optional(),
  isActive: z5.boolean().optional()
}).refine((input) => input.name !== void 0 || input.slug !== void 0 || input.isActive !== void 0);
var updateLocaleSchema = z5.object({ locale: localeSchema });
var setPasswordFormSchema = z5.object({ password: passwordSchema, confirmPassword: z5.string() }).refine((input) => input.password === input.confirmPassword, {
  message: VALIDATION_MESSAGES.passwordMismatch,
  path: ["confirmPassword"]
});
var mfaPasswordConfirmSchema = z5.object({ password: passwordSchema });
var totpCodeSchema = z5.object({
  code: z5.string().trim().regex(/^\d{6}$/)
});
var backupCodeSchema = z5.object({ code: z5.string().trim().min(1).max(64) });
var passkeyNameSchema = z5.object({
  name: z5.string().trim().min(1).max(IDENTITY_LIMITS.MAX_NAME_LENGTH)
});
var passkeySummarySchema = z5.object({
  id: z5.string().min(1),
  name: z5.string().nullable(),
  aaguid: z5.string().nullable()
}).readonly();
var updateProfileSchema = z5.object({ name: nameSchema });
var AVATAR = { MAX_BYTES: 1024 * 1024, CONTENT_TYPES: ["image/png", "image/jpeg", "image/webp"] };
var isAvatarContentType = (value) => AVATAR.CONTENT_TYPES.includes(value);
var EMAIL_PROVIDERS = ["graph", "smtp"];
var graphEmailSchema = z5.object({
  provider: z5.literal("graph"),
  tenantId: z5.string().trim().min(1).max(100),
  clientId: z5.string().trim().min(1).max(100),
  clientSecret: z5.string().max(1024),
  fromAddress: z5.email().max(320)
});
var smtpEmailSchema = z5.object({
  provider: z5.literal("smtp"),
  host: z5.string().trim().min(1).max(255),
  port: z5.number().int().min(1).max(65535),
  /** true = implicit TLS (465); false = STARTTLS (587). */
  secure: z5.boolean(),
  username: z5.string().trim().min(1).max(320),
  password: z5.string().max(1024),
  fromAddress: z5.email().max(320)
});
var emailTransportSchema = z5.discriminatedUnion("provider", [graphEmailSchema, smtpEmailSchema]);
var organizationEmailConfigSchema = z5.object({ transport: emailTransportSchema, enabled: z5.boolean() });
var emailTransportSummarySchema = z5.discriminatedUnion("provider", [
  graphEmailSchema.omit({ clientSecret: true }),
  smtpEmailSchema.omit({ password: true })
]);
var emailConfigSummarySchema = z5.object({ transport: emailTransportSummarySchema.nullable(), enabled: z5.boolean() }).readonly();
var emailTestSchema = z5.object({ to: z5.email() });

export {
  MAX_MESSAGE_KEY_LENGTH,
  messageRefSchema,
  msg,
  defineMessages,
  declaredMessageKeys,
  CORE_MESSAGES,
  VALIDATION_MESSAGES,
  IDENTITY_MESSAGES,
  SSO_PROVIDERS,
  SSO_PROVIDER_IDS,
  baSsoProviderId,
  ssoOidcRedirectUri,
  catalogSsoProviderId,
  ssoIssuerFor,
  tenantIdFromIssuer,
  ssoDiscoveryOrigins,
  LOCALES,
  MAX_LOCALE_LENGTH,
  localeSchema,
  matchLocale,
  ERROR_CODES,
  fieldIssueSchema,
  AppError,
  isAppError,
  ErrorFactories,
  toMessageRef,
  MAX_ID_LENGTH,
  createRegistry,
  requireOrganizationId,
  PLATFORM_ROLES,
  PLATFORM_ROLE,
  ORG_ROLE,
  ORG_ROLE_LABEL,
  ORG_RESOURCE,
  ACTION,
  can,
  AUTH_METHODS,
  PASSWORD_MFA,
  organizationAuthPolicySchema,
  DEFAULT_ORG_AUTH_POLICY,
  PLATFORM_AUTH_POLICY,
  resolveCompliant,
  callerSessionSchema,
  IDENTITY_LIMITS,
  passwordSchema,
  emailSchema,
  createOrganizationSchema,
  editOrganizationSchema,
  updateOrgModulesSchema,
  createOrgUserSchema,
  createPlatformAdminSchema,
  createOrganizationFormSchema,
  updateMemberSchema,
  memberRolesFormSchema,
  orgRoleFormSchema,
  orgRolePermissionsSchema,
  orgRoleSchema,
  ssoProviderSchema,
  ssoProviderFormSchema,
  signInFieldsSchema,
  signInFormSchema,
  ssoAvailableQuerySchema,
  ssoAvailableSchema,
  emailDomain,
  updateOrganizationSchema,
  updateLocaleSchema,
  setPasswordFormSchema,
  mfaPasswordConfirmSchema,
  totpCodeSchema,
  backupCodeSchema,
  passkeyNameSchema,
  passkeySummarySchema,
  updateProfileSchema,
  AVATAR,
  isAvatarContentType,
  EMAIL_PROVIDERS,
  emailTransportSchema,
  organizationEmailConfigSchema,
  emailTestSchema
};
//# sourceMappingURL=chunk-ONOJXEKY.js.map